import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import { cleanParams } from '../../../../../../../shared/utils/cleanParams';
import { getDateRangeValidationError } from '../../../../../../../shared/utils/time';

import type { IGetAsCsTemplateListParams } from '../../../../../../../shared/apis/ai-agent/template/types';
import type { IAiAgentTemplateFilterForm } from '../types';

const initialFilterForm: IAiAgentTemplateFilterForm = {
  startDate: null,
  endDate: null,
  keyword: '',
};

const parseDateParam = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  return new Date(`${value}Z`);
};

const formatDateParam = (date: Date | null, isEndOfDay = false): string => {
  if (!date) {
    return '';
  }

  return (isEndOfDay ? dayjs(date).endOf('day') : dayjs(date).startOf('day')).toISOString().substring(0, 19);
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export default function useAiAgentTemplateFilters(companyId: number) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IAiAgentTemplateFilterForm>(initialFilterForm);

  const startDateParam = searchParams.get('modStartDt') || searchParams.get('startDate') || '';
  const endDateParam = searchParams.get('modEndDt') || searchParams.get('endDate') || '';
  const keywordParam = searchParams.get('keyword') || '';

  const queryParams: IGetAsCsTemplateListParams = {
    companyId,
    pg: parsePositiveInteger(searchParams.get('page'), 1),
    pgSize: parsePositiveInteger(searchParams.get('size'), 10),
    name: keywordParam || undefined,
    startDt: startDateParam || undefined,
    endDt: endDateParam || undefined,
  };

  const updateFilterForm = (nextFilter: Partial<IAiAgentTemplateFilterForm>) => {
    setFilterForm((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const applyFilters = () => {
    const dateRangeValidationError = getDateRangeValidationError(filterForm.startDate, filterForm.endDate);

    if (dateRangeValidationError) {
      void Swal.fire({
        icon: 'error',
        ...dateRangeValidationError,
        confirmButtonText: '확인',
      });

      return;
    }

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        modStartDt: formatDateParam(filterForm.startDate),
        modEndDt: formatDateParam(filterForm.endDate, true),
        keyword: filterForm.keyword.trim(),
      }),
    );
  };

  const resetFilters = () => {
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        modStartDt: '',
        modEndDt: '',
        keyword: '',
      }),
    );
  };

  useEffect(() => {
    setFilterForm({
      startDate: parseDateParam(startDateParam),
      endDate: parseDateParam(endDateParam),
      keyword: keywordParam,
    });
  }, [endDateParam, keywordParam, startDateParam]);

  return {
    filterForm,
    queryParams,
    updateFilterForm,
    applyFilters,
    resetFilters,
  };
}
